(() => {
  /** Jenny's story switches between the founder profile and her product vision. */
  const initJenny = (root, signal) => {
    const section = root.querySelector('[data-jenny-carousel]');
    const nextButton = section?.querySelector('[data-jenny-next]');
    const previousButton = section?.querySelector('[data-jenny-previous]');
    const profile = section?.querySelector('.about-jenny__row');
    const insights = section?.querySelector('.about-jenny__insights');
    if (!section || !nextButton || !previousButton) return;

    const video = initJennyVideo(section, signal);

    const showInsights = (visible, moveFocus = true) => {
      section.classList.toggle('is-insights', visible);
      nextButton.setAttribute('aria-pressed', String(visible));
      profile?.setAttribute('aria-hidden', String(visible));
      insights?.setAttribute('aria-hidden', String(!visible));
      // Leaving the profile slide only suspends the video: its frame and progress
      // are kept, so coming back continues where it stopped.
      video?.setSlideVisible(!visible);
      if (moveFocus) (visible ? previousButton : nextButton).focus();
    };

    nextButton.addEventListener('click', () => showInsights(true), { signal });
    previousButton.addEventListener('click', () => showInsights(false), { signal });

    // Keep the arrow controls, while also supporting the native left/right
    // gesture expected on touch devices.
    let touchStartX = null;
    let touchStartY = null;
    const slides = section.querySelector('[data-jenny-slides]');
    slides?.addEventListener('touchstart', (event) => {
      const touch = event.changedTouches[0];
      touchStartX = touch?.clientX ?? null;
      touchStartY = touch?.clientY ?? null;
    }, { passive: true, signal });
    slides?.addEventListener('touchend', (event) => {
      if (touchStartX === null || touchStartY === null) return;
      const touch = event.changedTouches[0];
      const deltaX = (touch?.clientX ?? touchStartX) - touchStartX;
      const deltaY = (touch?.clientY ?? touchStartY) - touchStartY;
      touchStartX = null;
      touchStartY = null;
      if (Math.abs(deltaX) < 40 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
      if (deltaX < 0) showInsights(true, false);
      else showInsights(false, false);
    }, { passive: true, signal });
    slides?.addEventListener('touchcancel', () => {
      touchStartX = null;
      touchStartY = null;
    }, { passive: true, signal });
    showInsights(false, false);
  };

  /** What We Build is a three-card carousel with a centered active card. */
  const initBuildCarousel = (root, signal) => {
    const carousel = root.querySelector('[data-build-carousel]');
    if (!carousel) return;

    const cards = [...carousel.querySelectorAll('[data-build-card]')]
      .sort((first, second) => Number(first.dataset.buildOrder) - Number(second.dataset.buildOrder));
    const previousButton = carousel.querySelector('[data-build-previous]');
    const nextButton = carousel.querySelector('[data-build-next]');
    const viewport = carousel.querySelector('.about-build__viewport');
    if (cards.length < 2 || !previousButton || !nextButton) return;

    let activeIndex = Math.min(1, cards.length - 1);
    const update = () => {
      cards.forEach((card, index) => {
        const offset = index - activeIndex;
        card.style.left = `${50 + offset * 50.4226042}%`;
        card.classList.toggle('is-active', offset === 0);
        card.classList.toggle('is-previous', offset === -1);
        card.classList.toggle('is-next', offset === 1);
        card.classList.toggle('is-hidden', Math.abs(offset) > 1);
        card.setAttribute('aria-hidden', String(index !== activeIndex));
      });
      previousButton.disabled = activeIndex === 0;
      nextButton.disabled = activeIndex === cards.length - 1;
    };

    previousButton.addEventListener('click', (event) => {
      event.currentTarget.blur();
      if (activeIndex === 0) return;
      activeIndex -= 1;
      update();
      requestAnimationFrame(() => previousButton.blur());
    }, { signal });
    nextButton.addEventListener('click', (event) => {
      event.currentTarget.blur();
      if (activeIndex === cards.length - 1) return;
      activeIndex += 1;
      update();
      requestAnimationFrame(() => nextButton.blur());
    }, { signal });

    const clearButtonFocus = (event) => {
      // Touch UAs can keep the pressed/focus state after the pointer is up.
      if (event.pointerType === 'touch' || event.type === 'touchend') {
        event.currentTarget.blur();
      }
    };
    [previousButton, nextButton].forEach((button) => {
      button.addEventListener('pointerup', clearButtonFocus, { signal });
      button.addEventListener('touchend', clearButtonFocus, { passive: true, signal });
    });

    // Keep the arrow controls, while allowing a native horizontal swipe on
    // the mobile viewport.
    let touchStartX = null;
    let touchStartY = null;
    viewport?.addEventListener('touchstart', (event) => {
      const touch = event.changedTouches[0];
      touchStartX = touch?.clientX ?? null;
      touchStartY = touch?.clientY ?? null;
    }, { passive: true, signal });
    viewport?.addEventListener('touchend', (event) => {
      if (touchStartX === null || touchStartY === null) return;
      const touch = event.changedTouches[0];
      const deltaX = (touch?.clientX ?? touchStartX) - touchStartX;
      const deltaY = (touch?.clientY ?? touchStartY) - touchStartY;
      touchStartX = null;
      touchStartY = null;
      if (Math.abs(deltaX) < 40 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
      if (deltaX < 0 && activeIndex < cards.length - 1) activeIndex += 1;
      if (deltaX > 0 && activeIndex > 0) activeIndex -= 1;
      update();
    }, { passive: true, signal });
    viewport?.addEventListener('touchcancel', () => {
      touchStartX = null;
      touchStartY = null;
    }, { passive: true, signal });
    update();
  };


  /**
   * The Jenny video is click-to-play: the founder photo stays visible as the
   * poster (with the play badge) until the visitor starts playback.
   *
   * After that first click the video owns the frame: pausing — by scrolling
   * away, changing slide, or switching tabs — freezes the current frame instead
   * of covering it with the poster again, and playback resumes from there. Only
   * the end of the video or leaving the page returns to the poster.
   */
  const initJennyVideo = (section, signal) => {
    const container =
      section.querySelector('[data-jenny-media]') || section.querySelector('.about-jenny__media');
    const video = container?.querySelector('.about-jenny__video');
    if (!container || !video) return null;

    // Enforce the paused-by-default state regardless of the section settings.
    video.autoplay = false;
    // Once started, Jenny's clip keeps looping instead of returning to the poster.
    video.loop = true;
    video.controls = false;
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.pause();

    // `started` means the video already replaced the poster, `wantsPlayback` is
    // the visitor's intent to keep it running. A pause clears the intent but
    // keeps the frame; only `reset` puts the poster back.
    let started = false;
    let wantsPlayback = false;
    let inViewport = false;
    let onProfileSlide = true;

    const syncUi = () => {
      const playing = started && !video.paused && !video.ended;
      container.classList.toggle('is-playing', started);
      // The play badge comes back whenever the frozen frame is on screen.
      container.classList.toggle('is-paused', started && !playing);
      container.setAttribute('aria-pressed', String(playing));
      container.setAttribute('aria-label', playing ? '暂停视频' : '播放视频');
    };

    /** `play()` can still be rejected by the autoplay policy; the frame stays then. */
    const requestPlay = () => {
      video.muted = false;
      video.volume = 1;

      const playRequest = video.play();

      if (playRequest !== undefined) {
        playRequest.catch(() => syncUi());
      }
    };

    const startPlayback = () => {
      if (!started) {
        started = true;
        syncUi();
      }
      wantsPlayback = true;
      requestPlay();
    };

    /** Keeps playback in sync with the visitor's intent and the viewport. */
    const syncPlayback = () => {
      if (!wantsPlayback) return;

      const canPlay = inViewport && onProfileSlide && document.visibilityState !== 'hidden';

      if (canPlay) {
        if (video.paused && !video.ended) requestPlay();
        return;
      }

      // Suspending keeps the frame, so coming back continues where it stopped.
      if (!video.paused) video.pause();
    };

    /** Back to the poster, ready for a fresh click. */
    const reset = () => {
      wantsPlayback = false;
      started = false;
      video.pause();
      if (video.currentTime !== 0) video.currentTime = 0;
      syncUi();
    };

    const toggle = () => {
      if (video.paused || video.ended) {
        startPlayback();
        return;
      }

      // A manual pause keeps the frame and the progress.
      wantsPlayback = false;
      video.pause();
    };

    container.addEventListener('click', toggle, { signal });
    container.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      toggle();
    }, { signal });

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        const [entry] = entries;
        if (!entry) return;
        inViewport = entry.isIntersecting && entry.intersectionRatio >= 0.35;
        syncPlayback();
      }, { threshold: [0.35] });
      observer.observe(container);
      signal.addEventListener('abort', () => observer.disconnect());
    } else {
      inViewport = true;
    }

    // A background tab should not keep playing; coming back resumes.
    document.addEventListener('visibilitychange', syncPlayback, { signal });

    // Restored from the back/forward cache means the visitor left and returned.
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) reset();
    }, { signal });

    // Dropping out of the DOM (soft navigation, theme editor reload) ends the visit.
    signal.addEventListener('abort', reset);

    // The element's own state owns the poster/frame swap.
    video.addEventListener('play', syncUi, { signal });
    video.addEventListener('pause', syncUi, { signal });
    video.addEventListener('ended', reset, { signal });

    syncUi();

    return {
      reset,
      /** The profile slide owns the video; the insights slide suspends it. */
      setSlideVisible: (visible) => {
        onProfileSlide = visible;
        syncPlayback();
      },
    };
  };

  /** Lifecycle-safe component for Shopify's live theme editor. */
  class BeamboxAbout extends HTMLElement {
    connectedCallback() {
      if (this.abortController) return;
      this.abortController = new AbortController();
      initJenny(this, this.abortController.signal);
      initBuildCarousel(this, this.abortController.signal);
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.abortController = null;
    }
  }

  if (!customElements.get('beambox-about')) {
    customElements.define('beambox-about', BeamboxAbout);
  }
})();
